import React, { useState, useEffect } from 'react';
import { dashboardApi } from '../api/dashboard';
import { 
  Bell, AlertTriangle, AlertCircle, Clock, X, CreditCard, Banknote,
  ChevronRight, RefreshCw
} from 'lucide-react';
import { formatCurrency } from '../utils/format';
import { useNavigate } from 'react-router-dom';

export default function NotificationPanel({ isOpen, onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getNotifications();
      setNotifications(data.notifications);
      setSummary(data.summary);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: <AlertTriangle className="text-red-500" size={20} />,
          badge: 'bg-red-100 text-red-800'
        };
      case 'high':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          icon: <AlertCircle className="text-orange-500" size={20} />,
          badge: 'bg-orange-100 text-orange-800'
        };
      case 'medium':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: <Clock className="text-yellow-500" size={20} />,
          badge: 'bg-yellow-100 text-yellow-800'
        };
      default:
        return {
          bg: 'bg-stone-50',
          border: 'border-stone-200',
          icon: <Bell className="text-slate-500" size={20} />,
          badge: 'bg-slate-100 text-slate-800'
        };
    }
  };

  const handleClick = (notification) => {
    if (notification.type === 'debt') {
      navigate('/debts');
    } else if (notification.type === 'receivable') {
      navigate('/receivables');
    } else if (notification.type === 'tax') {
      navigate('/taxes');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div 
        className="relative w-full max-w-md bg-white h-full shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <Bell size={20} className="text-slate-700" />
            <h2 className="text-lg font-semibold text-[#0F172A]">Notifikasi</h2>
            {summary && summary.total > 0 && (
              <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                {summary.total}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={loadNotifications}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RefreshCw size={18} className={`text-slate-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={18} className="text-slate-500" />
            </button>
          </div>
        </div>

        {/* Summary */}
        {summary && summary.total > 0 && (
          <div className="p-4 bg-stone-50 border-b border-stone-200">
            <div className="flex gap-4">
              {summary.critical > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full" />
                  <span className="text-sm text-slate-600">{summary.critical} Kritis</span>
                </div>
              )}
              {summary.high > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-orange-500 rounded-full" />
                  <span className="text-sm text-slate-600">{summary.high} Tinggi</span>
                </div>
              )}
              {summary.medium > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-yellow-500 rounded-full" />
                  <span className="text-sm text-slate-600">{summary.medium} Sedang</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notification List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="animate-spin mx-auto mb-2 text-slate-400" size={24} />
              <p className="text-slate-500">Memuat notifikasi...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="mx-auto mb-2 text-slate-300" size={48} />
              <p className="text-slate-500">Tidak ada notifikasi</p>
              <p className="text-sm text-slate-400 mt-1">Semua hutang & piutang dalam kondisi aman</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map((notification) => {
                const styles = getPriorityStyles(notification.priority);
                return (
                  <button
                    key={`${notification.type}-${notification.id}`}
                    onClick={() => handleClick(notification)}
                    className={`w-full p-4 text-left hover:bg-stone-50 transition-colors ${styles.bg}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${notification.type === 'debt' ? 'bg-red-100' : 'bg-yellow-100'}`}>
                        {notification.type === 'debt' ? (
                          <CreditCard size={20} className="text-red-600" />
                        ) : (
                          <Banknote size={20} className="text-yellow-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles.badge}`}>
                            {notification.priority === 'critical' ? 'KRITIS' : 
                             notification.priority === 'high' ? 'TINGGI' : 'SEDANG'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {notification.type === 'debt' ? 'Hutang' : 'Piutang'}
                          </span>
                        </div>
                        <p className="font-medium text-[#0F172A] text-sm">{notification.title}</p>
                        <p className="text-sm text-slate-600 mt-0.5">{notification.message}</p>
                        <p className="text-lg font-bold text-[#0F172A] mt-1">
                          {formatCurrency(notification.amount)}
                        </p>
                      </div>
                      <ChevronRight size={18} className="text-slate-400 mt-2" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
